import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { TRADE, CONSUMER } from 'dibs-buyer-layout/exports/userTypeEnums.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { getProductTileAdMap } from '../../utils/adHelpers.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import {
    RESP_ITEMS_PER_ROW_5,
    RESP_ITEMS_PER_ROW,
    MOBILE_ITEMS_PER_ROW,
} from '../../constants/sbConstants.ts';

import {
    type useMergedSbAndSponsoredItems_itemSearch$key,
    type useMergedSbAndSponsoredItems_itemSearch$data,
} from './__generated__/useMergedSbAndSponsoredItems_itemSearch.graphql.ts';

type UseMergedSbAndSponsoredItemsArgs = {
    itemSearch: useMergedSbAndSponsoredItems_itemSearch$key;
};

type Sponsored = NonNullable<useMergedSbAndSponsoredItems_itemSearch$data['sponsored']>;
export type SponsoredMetadata = NonNullable<Sponsored['metadata']>;

// S&B item and sponsored item should the the same
export type Item = NonNullable<NonNullable<Sponsored['items']>[number]>;

export type SponsoredEntryMetadataMap = Record<
    string | number,
    Omit<SponsoredMetadata[number], 'itemId' | ' $fragmentType'>
>;

const itemSearchFragment = graphql`
    fragment useMergedSbAndSponsoredItems_itemSearch on ItemSearchQueryConnection {
        displayUriRef
        sponsored {
            items {
                serviceId
                ...SbRespSearchResultContainer_item
                ...SbMobileSearchResultContainer_item
            }
            metadata {
                itemId
                impressionTrackerLink
                clickTrackerLink
            }
        }
        edges {
            node {
                item {
                    serviceId
                    ...SbRespSearchResultContainer_item
                    ...SbMobileSearchResultContainer_item
                }
            }
        }
    }
`;

export const DEFAULT_PAGE_SIZE = 60;
export const MIN_ITEMS_COUNT = 12;
//remove 24 from the array if 'quick_filters_5_wide' abTest is successful
export const SPONSORED_ROW_INDEX_RESP = [0, 4, 8, 12, 16, 20, 24];
export const SPONSORED_ROW_INDEX_MOBILE = [0, 5, 10, 15, 20, 25, 30, 35];

export const useMergedSbAndSponsoredItems = ({
    itemSearch: itemSearchRef,
}: UseMergedSbAndSponsoredItemsArgs): {
    mergedItems: Item[];
    sponsoredItemsIndexMetadataMap?: SponsoredEntryMetadataMap;
} => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { edges, sponsored, displayUriRef } = itemSearch;
    const { items, metadata } = sponsored || {};

    const { first: pageSize = DEFAULT_PAGE_SIZE, isTrade } = useSbSelector(
        state => state.relayVariables.variables
    );

    const { areDisplayAdsEnabled, isDesktop, isQuickFiltersRedesignVariant } =
        useServerVarsContext();

    const pageSizeNumber = typeof pageSize === 'string' ? parseInt(pageSize) : pageSize;

    const sbItems = useMemo(
        () => (edges || []).map(edge => edge?.node?.item).filter(filterFalsy),
        [edges]
    );
    const sponsoredItems = useMemo(() => (items || []).filter(filterFalsy), [items]);
    const sponsoredMetadata = useMemo(() => (metadata || []).filter(filterFalsy), [metadata]);

    // If there aren't any items, don't attempt merge
    // If item count is less than min needed for sponsored listings, return items
    const areInvalidSbItems =
        !sbItems ||
        (Array.isArray(sbItems) && (sbItems.length === 0 || sbItems.length < MIN_ITEMS_COUNT));

    // If there aren't any sponsored items, return items
    const areInvalidSponsoredItems =
        !sponsoredItems || (Array.isArray(sponsoredItems) && sponsoredItems.length === 0);

    // If we're missing any required arguments or they are invalid, don't attempt merge
    const areInvalidArgs =
        typeof pageSizeNumber !== 'number' || isNaN(pageSizeNumber) || typeof isTrade !== 'boolean';

    const shouldNotMerge = areInvalidSbItems || areInvalidSponsoredItems || areInvalidArgs;

    const mergedResult = useMemo(() => {
        if (shouldNotMerge) {
            return { mergedItems: sbItems };
        }

        let itemsPerRow = isDesktop ? RESP_ITEMS_PER_ROW : MOBILE_ITEMS_PER_ROW;
        if (isQuickFiltersRedesignVariant && isDesktop) {
            itemsPerRow = RESP_ITEMS_PER_ROW_5;
        }

        const adMap = getProductTileAdMap({
            uriRef: displayUriRef || '',
            pageSize: pageSizeNumber,
            pageCol: itemsPerRow,
            userType: isTrade ? TRADE : CONSUMER,
            deviceType: isDesktop ? 'desktop' : 'mobile',
            areDisplayAdsEnabled,
            isQuickFiltersRedesignVariant,
        });

        const metadataMap = sponsoredMetadata.reduce<SponsoredEntryMetadataMap>((acc, value) => {
            if (value && typeof value.itemId === 'string') {
                const { itemId, clickTrackerLink, impressionTrackerLink } = value;
                acc[itemId] = { clickTrackerLink, impressionTrackerLink };
            }

            return acc;
        }, {});
        const mergedItems = [];

        // We don't count Product Tile ads on mobile towards the slots because they take up their own row
        let adCount = isDesktop ? Object.entries(adMap).length : 0;

        if (isDesktop) {
            // If ad index is greater than amount of items, decrease ad total count
            const totalItems =
                sbItems.length + sponsoredItems.length - (sponsoredItems.length % itemsPerRow);
            for (const prop in adMap) {
                if (Object.hasOwn(adMap, prop)) {
                    const adPos = parseInt(prop);
                    if (adPos >= totalItems) {
                        adCount--;
                    }
                }
            }
        }

        const totalSlots = sbItems.length + sponsoredItems.length + adCount;
        const sponsoredRowIndex = isDesktop ? SPONSORED_ROW_INDEX_RESP : SPONSORED_ROW_INDEX_MOBILE;

        let currRow = 0;
        let sponsoredRow: Item[] = [];
        let sponsoredItemsIndex = 0;
        let itemsIndex = 0;

        const sponsoredItemsIndexMetadataMap: SponsoredEntryMetadataMap = {};

        for (let i = 0; i < totalSlots; i++) {
            if (i !== 0 && i % itemsPerRow === 0) {
                currRow++;
            }

            if (
                sponsoredRowIndex.includes(currRow) &&
                sponsoredItemsIndex < sponsoredItems.length
            ) {
                const sponsoredItem = sponsoredItems[sponsoredItemsIndex];
                sponsoredRow.push(sponsoredItem);
                sponsoredItemsIndex++;
            } else if (adMap[i + 1]) {
                // Do not add SB item to merged items for desktop, as this index is reserved for Product Tile ad
                // On MW Product Tile ads take up their own row, so adding SB item
                if (!isDesktop) {
                    mergedItems.push(sbItems[itemsIndex]);
                    itemsIndex++;
                }
            } else if (itemsIndex < sbItems.length) {
                mergedItems.push(sbItems[itemsIndex]);
                itemsIndex++;
            } else {
                // We ran off SB items, and next item is sponsored, but it's not a sponsored row.
                break;
            }

            if (sponsoredRow.length === itemsPerRow) {
                let counter = 0;
                while (counter < itemsPerRow) {
                    const sponsoredItem = sponsoredRow[counter];
                    const mapIndex = mergedItems.length + counter;
                    const itemId = sponsoredItem?.serviceId;
                    if (itemId) {
                        sponsoredItemsIndexMetadataMap[mapIndex] = metadataMap[itemId];
                    }

                    counter++;
                }
                mergedItems.push(...sponsoredRow);
                if (itemsIndex >= sbItems.length) {
                    // We ran off SB items, so pushing last row of sponsored items and stop merging
                    break;
                }
                sponsoredRow = [];
            }
        }

        return { mergedItems, sponsoredItemsIndexMetadataMap };
    }, [
        shouldNotMerge,
        displayUriRef,
        isDesktop,
        isTrade,
        pageSizeNumber,
        sbItems,
        sponsoredItems,
        sponsoredMetadata,
        areDisplayAdsEnabled,
        isQuickFiltersRedesignVariant,
    ]);

    return mergedResult;
};
